import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { FormControl, InputGroup, Tab, Tabs } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getSortCodes } from '../../api/SortCodes';
import { getSummaryReportOptions, saveSummaryReportOptions } from '../../api/SummaryReportOptions';
import { WaitModal } from '../../components/ReportModals';
import { ISortCode, ISummaryReportOptions } from '../../util/DataModelInterface';
import { getCurrentYear } from '../../util/DateUtils';

interface ISummaryReportOptionsProps {
  intl: any;
}

const emptyOptions: ISummaryReportOptions = {
  coolContainerMonthlyCost: 0,
  ldaOpenContainerMonthlyCost: 0,
  receivedMonthlyCostPerKilo: 0,
  sdaCeCrtContainerMonthlyCost: 0,
  validityYear: getCurrentYear()
}

const SummaryReportOptions = (props: ISummaryReportOptionsProps) => {
  const [, setState] = useState({})
  const [options, setOptions] = useState([] as ISummaryReportOptions[]);
  const [sortCodes, setSortCodes] = useState([] as ISortCode[]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [currentOptions, setCurrentOptions] = useState(emptyOptions);
  const [isYearEditable, setYearEditable] = useState(false);
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [modalSaveOpen, setSaveModalOpen] = useState(false);
  const [changed, setChanged] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tab, setTab] = useState("yearValues");
  
  useEffect(() => {
    setWaitModalOpen(true);
    getSummaryReportOptions().then((data: any) => {
      if (data && Array.isArray(data)) {
        const list = data as ISummaryReportOptions[];
        const sorted = list.sort((a: ISummaryReportOptions, b: ISummaryReportOptions) =>
          b.validityYear.localeCompare(a.validityYear) // newest first
        );
        setOptions(sorted);
        setCurrentOptions(sorted.find((o) => o.validityYear === getCurrentYear()) || emptyOptions);
        setState({});
      }
      setWaitModalOpen(false);
    });
    getSortCodes().then((data: any) => {
      if (data && Array.isArray(data)) {
        setSortCodes(data.sort((a: ISortCode, b: ISortCode) => a.codeId - b.codeId));
      }
    })
  }, []);

  const getOptionRow = (labelKey: string,
                        field: keyof ISummaryReportOptions,
                        unitKey: string,
                        readOnly = false,
                        isLabel = false) => {
    let precision = field === 'receivedMonthlyCostPerKilo' ? 4 : 0;
    let value = typeof currentOptions[field] === 'number' ? props.intl.formatNumber(currentOptions[field], { maximumFractionDigits: precision, minimumFractionDigits: precision }) : currentOptions[field];

    return (
    <div className="optionRow form-group">
      {isLabel ?
      <label><FormattedMessage id={labelKey}/></label>
      :
      <label className="rowLabel"><FormattedMessage id={labelKey}/></label>}
      <InputGroup>
        <FormControl  value={value} className="form-control" readOnly = {readOnly}
                      onChange={(event: any) => {
                        setCurrentOptions({...currentOptions, [field]: event.target.value})
                        setChanged(true);
                      }}
                      onBlur={(event: any) => {
                        let newValue = parseFloat(event.target.value.replace(/\s/g, "").replace(/,/g, "."));
                        if(isNaN(newValue)) newValue = 0;
                        setCurrentOptions({...currentOptions, [field]: newValue })
                      }}/>
        <InputGroup.Text className="input-group-append"><FormattedMessage id={unitKey}/></InputGroup.Text>
      </InputGroup>
    </div>
    );
  }

  const formatEditedDate = (date: any) => {
    if (date !== undefined) {
      const d: Date = new Date(date);
      return d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();
    } else {
      return '';
    }
  }

  const resetValues = () => {
    setCurrentOptions(options.find((o) => o.validityYear === selectedYear) || emptyOptions);
    setYearEditable(false);
    setChanged(false);
  }

  const saveValues = async () => {
    setSaveModalOpen(true);
    saveSummaryReportOptions(currentOptions).then((data: any) => {
      if (data && Array.isArray(data)) {
        const list = data as ISummaryReportOptions[];
        const sorted = list.sort((a: ISummaryReportOptions, b: ISummaryReportOptions) =>
          b.validityYear.localeCompare(a.validityYear) // newest first
        );
        setOptions(sorted);
        setYearEditable(false);
        setChanged(false);
        setState({});
      }
      setSaveModalOpen(false);
    });
  }

  const getYearValues = () => {
    const years = options.map((o: ISummaryReportOptions) => {
      const classes = ['nav-item nav-link yearTab'];
      if (o.validityYear === selectedYear) {
        classes.push('selected');
      }
      return (
        // eslint-disable-next-line no-script-url, jsx-a11y/anchor-is-valid
        <a href="#" className={classes.join(' ')} key={o.validityYear}
          onClick={() => { setSelectedYear(o.validityYear);
                           setCurrentOptions(o); }}>
          {o.validityYear}
        </a>
      );
    });
    const values = currentOptions;
    let total = 0;
    try {
      total = Number.parseInt(values.coolContainerMonthlyCost.toString())
        + Number.parseInt(values.ldaOpenContainerMonthlyCost.toString())
        + Number.parseInt(values.sdaCeCrtContainerMonthlyCost.toString())
    } catch (e) {
      // invalid values
    }

    values.total = total;
    return (
      <>
        <div className="yearTabs settings">
          <nav className="nav nav-tabs" role="tablist">
            <div className="addYear ">
              <button className="btn btn-primary"
                      onClick={() => {
                        setSelectedYear('');
                        setYearEditable(true);
                        setCurrentOptions(emptyOptions);
                      }}>
                <FontAwesomeIcon icon="plus-circle"/>
              </button>
            </div>
            { isYearEditable &&
              <div className="yearTab nav-link nav-item selected">
                <input  type="number" min="0"
                        className="newYear form-control"
                        value={selectedYear}
                        onChange={(e) => {
                          setSelectedYear(e.target.value);
                          setCurrentOptions({...currentOptions, validityYear: e.target.value});
                        }}/>
              </div>}
            { years }
          </nav>
        </div>
        <div className="yearOptions">
          <div className="MainForm search-wrap">
            <div className="valuesColumn">
              <h3><FormattedMessage id="sortsummaryreports.settings.containercosts"/></h3>
              { getOptionRow('sortsummaryreports.settings.lda',
                    'ldaOpenContainerMonthlyCost',
                    'sortsummaryreports.settings.eur') }
              { getOptionRow('sortsummaryreports.settings.cool',
                    'coolContainerMonthlyCost',
                    'sortsummaryreports.settings.eur') }
              { getOptionRow('sortsummaryreports.settings.sda',
                    'sdaCeCrtContainerMonthlyCost',
                    'sortsummaryreports.settings.eur') }
              { getOptionRow('sortsummaryreports.settings.total',
                    'total',
                    'sortsummaryreports.settings.eur',
                    true) }
              { getOptionRow('sortsummaryreports.settings.receptioncost',
                    'receivedMonthlyCostPerKilo',
                    'sortsummaryreports.settings.eurkg',
                    false, true) }
            </div>
            <div className="form-buttons d-flex flex-row">
              <div className="edited">
                <FormattedMessage id="sortsummaryreports.settings.edited"/><br/>{ formatEditedDate(values.created) }
              </div>
              <div className="buttons d-flex flex-row ml-md-auto">
                <button className="btn btn-secondary" type="reset" disabled={!changed} onClick={() => resetValues()}>
                  <FormattedMessage id="sortsummaryreports.settings.reset"/>
                </button>
                <button className="btn btn-primary" type="button"  disabled={!changed} onClick={() => saveValues()}>
                  <FormattedMessage id="sortsummaryreports.settings.save"/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const getSortCodeTable = () => {
    const rows = sortCodes.map((c) => {
      if (c.codeId.toString().endsWith('00')) {
        return  (
          <tr key={c.codeId}>
            <td className="codeId"></td><td className="subheader">{c.groupName}</td>
          </tr>
        );
      } else {
        return  (
          <tr key={c.codeId}>
            <td className="codeId">{c.codeId}</td>
            <td className="groupName" title={c.description}>{c.groupName}</td>
          </tr>
        );
      }
    });
    return (
      <table className="sortCodeTable">
        <tbody>
          {rows}
        </tbody>
      </table>
    );
  }

  return (
    <div className="SummaryReportOptions">
      <WaitModal
        messageId="sortsummaryreports.settings.getting"
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}
      />
      <WaitModal
        messageId="sortsummaryreports.settings.saving"
        modalOpen={modalSaveOpen}
        setModalOpen={(open: boolean) => setSaveModalOpen(open)}
      />
      <h1>
        <FormattedMessage id="sortsummaryreports.costincome.header"/>
        <br/>
        <FormattedMessage id="sortsummaryreports.settings.header"/>
      </h1>
      <Tabs defaultActiveKey="yearValues" id="comparereports-manage-tabs" onSelect={(eventKey: any) => setTab(eventKey ? eventKey : "yearValues")}>
        <Tab eventKey="yearValues" title={props.intl.formatMessage({id:'summaryreport.options.tab1'})}>
          <div className="yearValues">
            { getYearValues() }
          </div>
        </Tab>
        <Tab eventKey="sortCodes" title={props.intl.formatMessage({id:'summaryreport.options.tab2'})}>
          <div className="sortCodes">
            <h3><FormattedMessage id="sortsummaryreports.settings.weeeclasses"/></h3>
            { getSortCodeTable() }
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export default injectIntl(SummaryReportOptions);